import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError, toDRXError } from '@/lib/drx-error';

/**
 * Storefront / curriculum seller profile documents (`sellers/{userId}`).
 */
export class SellerRepository {
  private readonly sellersCollection = 'sellers';

  private toError(error: unknown, code: string, devMessage: string) {
    return toDRXError(error, { code, devMessage });
  }

  async getSellerProfile(userId: string): Promise<Record<string, unknown> & { id: string } | null> {
    try {
      const snap = await getDoc(doc(db, this.sellersCollection, userId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() } as Record<string, unknown> & { id: string };
    } catch (error) {
      throw this.toError(error, 'SELLER_PROFILE_FETCH_FAILED', `Failed to load seller profile for ${userId}`);
    }
  }

  /** Full document write (store onboarding completion). */
  async setSellerProfile(userId: string, data: Record<string, unknown>): Promise<void> {
    try {
      await setDoc(doc(db, this.sellersCollection, userId), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw this.toError(error, 'SELLER_PROFILE_SET_FAILED', `Failed to save seller profile for ${userId}`);
    }
  }

  async mergeSellerProfile(userId: string, data: Record<string, unknown>): Promise<void> {
    try {
      await setDoc(
        doc(db, this.sellersCollection, userId),
        {
          ...data,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (error) {
      throw this.toError(error, 'SELLER_PROFILE_MERGE_FAILED', `Failed to merge seller profile for ${userId}`);
    }
  }

  async updateSellerProfile(userId: string, patch: Record<string, unknown>): Promise<void> {
    try {
      await updateDoc(doc(db, this.sellersCollection, userId), {
        ...patch,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw this.toError(error, 'SELLER_PROFILE_UPDATE_FAILED', `Failed to update seller profile for ${userId}`);
    }
  }

  /** Default analytics doc for `sellers/{userId}/sellerData/stats`. */
  async initializeSellerStats(userId: string): Promise<void> {
    try {
      await setDoc(doc(db, 'sellers', userId, 'sellerData', 'stats'), {
        totalSales: 0,
        totalRevenue: 0,
        totalDownloads: 0,
        totalResources: 0,
        storeViews: 0,
        avgRating: 0,
        lastUpdated: serverTimestamp(),
      });
    } catch (error) {
      throw new DRXError({
        code: 'SELLER_STATS_INIT_FAILED',
        devMessage: `Failed to initialize seller stats for ${userId}`,
        cause: error,
      });
    }
  }
}
